import React, { useState, useEffect } from 'react';
import api, { orderAPI } from '../api';
import { Order, OrderStatusHistory, ORDER_STATUSES } from '../types/order';
import { formatCurrency } from '../utils/formatNumber';
import ShipOrderModal, { ShipDetails } from './ShipOrderModal';

interface OrderDetailProps {
  orderId: number;
  onBack?: () => void;
  userRole?: string;
}

interface InventoryStock {
  product_id: number;
  warehouse_id: number;
  quantity_available: number;
  quantity_on_hand: number;
  quantity_reserved: number;
}

const OrderDetail: React.FC<OrderDetailProps> = ({ orderId, onBack, userRole = 'viewer' }) => {
  const [order, setOrder] = useState<Order | null>(null);
  const [statusHistory, setStatusHistory] = useState<OrderStatusHistory[]>([]);
  const [inventoryMap, setInventoryMap] = useState<Map<number, InventoryStock>>(new Map());
  const [loading, setLoading] = useState(true);
  const [statusUpdateLoading, setStatusUpdateLoading] = useState(false);
  const [warehouses, setWarehouses] = useState<any[]>([]);
  const [changingWarehouse, setChangingWarehouse] = useState(false);
  const [selectedWarehouseId, setSelectedWarehouseId] = useState<string>('');
  const [showShipModal, setShowShipModal] = useState(false);

  useEffect(() => {
    fetchOrderDetails();
    fetchWarehouses();
  }, [orderId]);

  const fetchWarehouses = async () => {
    try {
      const res = await api.get('/inventory/warehouses/');
      setWarehouses(Array.isArray(res.data) ? res.data : res.data?.warehouses || []);
    } catch (e) {}
  };

  const handleWarehouseChange = async (newWarehouseId: string) => {
    if (!order || !newWarehouseId) return;
    setChangingWarehouse(true);
    try {
      await api.put(`/orders/${order.id}`, { warehouse_id: parseInt(newWarehouseId) });
      setSelectedWarehouseId('');
      fetchOrderDetails();
    } catch (err: any) {
      alert(err.response?.data?.detail || 'Failed to update warehouse');
    } finally {
      setChangingWarehouse(false);
    }
  };

  const fetchOrderDetails = async () => {
    setLoading(true);
    try {
      // Fetch order details
      const orderResponse = await orderAPI.getOrder(orderId);
      const orderData = orderResponse.data;
      setOrder(orderData);

      // Fetch status history
      try {
        const historyResponse = await orderAPI.getOrderStatusHistory(orderId);
        setStatusHistory(historyResponse.data);
      } catch (error) {
        console.error('Error fetching status history:', error);
        setStatusHistory([]);
      }

      // Fetch inventory for each product
      if (orderData.items && orderData.warehouse_id) {
        fetchInventoryForProducts(orderData.items, orderData.warehouse_id);
      }
    } catch (error) {
      console.error('Error fetching order details:', error);
      alert('Failed to load order details');
    } finally {
      setLoading(false);
    }
  };

  const fetchInventoryForProducts = async (items: any[], warehouseId: number) => {
    const inventoryPromises = items.map(async (item) => {
      try {
        const response = await api.get('/inventory/stock/', {
          params: {
            product_id: item.product_id,
            warehouse_id: warehouseId
          }
        });
        if (response.data && response.data.length > 0) {
          return { productId: item.product_id, stock: response.data[0] };
        }
      } catch (error) {
        console.error(`Error fetching inventory for product ${item.product_id}:`, error);
      }
      return null;
    });

    const results = await Promise.all(inventoryPromises);
    const newInventoryMap = new Map<number, InventoryStock>();
    results.forEach(result => {
      if (result) {
        newInventoryMap.set(result.productId, result.stock);
      }
    });
    setInventoryMap(newInventoryMap);
  };

  const handleStatusChange = async (newStatus: string) => {
    if (!order) return;

    // Moving to "shipped" requires capturing shipping details (partner / hand
    // delivery / custom + AWB). Open the dedicated popup instead of a plain confirm.
    if (newStatus === 'shipped') {
      setShowShipModal(true);
      return;
    }

    // Hand-delivered orders carry a delivery reference / tracking number
    // (stored as the order's AWB). This is normally captured at ship-out; only
    // prompt for it here if it's still missing (e.g. delivered directly by hand
    // without a prior ship-out step).
    if (newStatus === 'delivered' && (order as any).delivery_method === 'hand_delivery' && !(order as any).awb_number) {
      const ref = window.prompt(
        'Enter the delivery reference / tracking number for this hand-delivered order (it will be saved as the order AWB):',
        ''
      );
      if (ref === null) return; // cancelled
      if (!ref.trim()) {
        alert('A delivery reference / tracking number is required for a hand-delivered order.');
        return;
      }
      await submitStatusChange(newStatus, undefined, ref.trim());
      return;
    }

    const confirmMessage = `Are you sure you want to change the order status to "${newStatus}"?`;
    if (!window.confirm(confirmMessage)) {
      return;
    }

    await submitStatusChange(newStatus);
  };

  const handleShipConfirm = async (details: ShipDetails) => {
    await submitStatusChange('shipped', details);
  };

  const submitStatusChange = async (newStatus: string, shipDetails?: ShipDetails, deliveryReference?: string) => {
    if (!order) return;

    setStatusUpdateLoading(true);
    try {
      await orderAPI.updateOrderStatus(orderId, {
        status: newStatus,
        notes: null,
        ...(shipDetails
          ? {
              delivery_method: shipDetails.delivery_method,
              shipping_provider: shipDetails.shipping_provider,
              awb_number: shipDetails.awb_number,
            }
          : {}),
        ...(deliveryReference ? { delivery_reference: deliveryReference } : {}),
      });

      setShowShipModal(false);
      alert(`Order status updated to "${newStatus}" successfully!`);
      fetchOrderDetails(); // Refresh order details
    } catch (error: any) {
      console.error('Error updating order status:', error);
      
      let errorMessage = 'Failed to update order status';
      
      if (error.response?.data?.detail) {
        const detail = error.response.data.detail;
        
        // Handle array of validation errors
        if (Array.isArray(detail)) {
          errorMessage = detail.map((e: any) => {
            if (typeof e === 'object' && e.msg) {
              return `${e.loc?.join('.') || 'Field'}: ${e.msg}`;
            }
            return String(e);
          }).join(', ');
        }
        // Handle string error
        else if (typeof detail === 'string') {
          errorMessage = detail;
        }
        // Handle object error
        else if (typeof detail === 'object') {
          errorMessage = detail.msg || JSON.stringify(detail);
        }
      } else if (error.message) {
        errorMessage = error.message;
      }
      
      alert(`Error: ${errorMessage}`);
    } finally {
      setStatusUpdateLoading(false);
    }
  };

  const handlePrint = () => {
    window.print();
  };

  const handleExport = async () => {
    try {
      const response = await orderAPI.exportOrders('csv', {
        order_number: order?.order_number
      });

      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `order_${order?.order_number || orderId}.csv`);
      document.body.appendChild(link);
      link.click();
      link.remove();
    } catch (error) {
      console.error('Error exporting order:', error);
      alert('Failed to export order');
    }
  };

  const handleDeleteOrder = async () => {
    if (!order) return;
    const confirmed = window.confirm(
      `Are you sure you want to permanently delete order ${order.order_number}? This action cannot be undone.`
    );
    if (!confirmed) return;
    
    try {
      await orderAPI.deleteOrder(orderId);
      alert(`Order ${order.order_number} deleted successfully.`);
      if (onBack) onBack();
    } catch (error: any) {
      const msg = error.response?.data?.detail || 'Failed to delete order';
      alert(msg);
    }
  };

  const getStatusBadge = (status: string) => {
    const statusColors: { [key: string]: string } = {
      pending: '#fbbf24',
      confirmed: '#60a5fa',
      allocated: '#a78bfa',
      shipped: '#34d399',
      delivered: '#10b981',
      cancelled: '#ef4444',
    };
    
    return (
      <span 
        style={{
          padding: '6px 16px',
          borderRadius: '16px',
          fontSize: '14px',
          fontWeight: '600',
          backgroundColor: statusColors[status] || '#9ca3af',
          color: 'white',
          textTransform: 'capitalize',
        }}
      >
        {status}
      </span>
    );
  };

  const formatDate = (dateString: string) => {
    const date = new Date(dateString);
    return date.toLocaleDateString('en-IN', { 
      year: 'numeric', 
      month: 'long', 
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const getAvailableStatusTransitions = (currentStatus: string): string[] => {
    const transitions: { [key: string]: string[] } = {
      pending: ['confirmed', 'cancelled'],
      confirmed: ['allocated', 'cancelled'],
      allocated: ['shipped', 'cancelled'],
      shipped: ['delivered'],
      delivered: [],
      cancelled: []
    };
    return transitions[currentStatus] || [];
  };

  if (loading) {
    return <div style={{ padding: '2rem', textAlign: 'center' }}>Loading order details...</div>;
  }

  if (!order) {
    return <div style={{ padding: '2rem', textAlign: 'center' }}>Order not found</div>;
  }

  const availableTransitions = getAvailableStatusTransitions(order.status || 'pending');

  return (
    <div style={{ maxWidth: '1200px', width: '100%' }}>
      {/* Back Button */}
      {onBack && (
        <div style={{ marginBottom: '1rem' }}>
          <button
            onClick={onBack}
            className="btn btn-secondary"
            style={{
              display: 'flex',
              alignItems: 'center',
              gap: '8px',
              padding: '8px 16px',
              fontSize: '14px',
              fontWeight: '600',
              background: '#6c757d',
              color: 'white',
              border: 'none',
              borderRadius: '6px',
              cursor: 'pointer'
            }}
          >
            <span>←</span> Back to Orders
          </button>
        </div>
      )}
      {/* Header Section */}
      <div style={{ 
        display: 'flex', 
        justifyContent: 'space-between', 
        alignItems: 'center',
        marginBottom: '2rem',
        paddingBottom: '1rem',
        borderBottom: '2px solid #e2e8f0'
      }}>
        <div>
          <h2 style={{ margin: 0, marginBottom: '0.5rem' }}>Order {order.order_number}</h2>
          <div style={{ color: '#64748b', fontSize: '14px' }}>
            Created on {formatDate(order.created_at)}
          </div>
        </div>
        <div style={{ display: 'flex', gap: '0.5rem' }}>
          {userRole === 'admin' && (
            <button 
              className="btn btn-danger" 
              onClick={handleDeleteOrder}
              style={{ background: '#dc3545', color: 'white', border: 'none', borderRadius: '6px', padding: '8px 16px', fontWeight: '600', cursor: 'pointer' }}
            >
              🗑️ Delete
            </button>
          )}
          <button className="btn btn-secondary" onClick={handlePrint}>
            🖨️ Print
          </button>
          <button className="btn btn-secondary" onClick={handleExport}>
            📥 Export
          </button>
        </div>
      </div>

      {/* Order Header Information */}
      <div style={{ 
        display: 'grid', 
        gridTemplateColumns: 'repeat(auto-fit, minmax(250px, 1fr))',
        gap: '1.5rem',
        marginBottom: '2rem'
      }}>
        <div style={{ 
          padding: '1.5rem', 
          backgroundColor: '#f8fafc', 
          borderRadius: '8px',
          border: '1px solid #e2e8f0'
        }}>
          <h4 style={{ margin: 0, marginBottom: '1rem', color: '#475569' }}>Order Status</h4>
          <div style={{ marginBottom: '1rem' }}>
            {getStatusBadge(order.status || 'pending')}
          </div>
          <div style={{ fontSize: '14px', color: '#64748b' }}>
            <div><strong>Payment:</strong> {order.payment_status || 'pending'}</div>
            <div><strong>Order Date:</strong> {formatDate(order.order_date)}</div>
            {(order as any).delivered_date && <div><strong>Delivered Date:</strong> {formatDate((order as any).delivered_date)}</div>}
          </div>
        </div>

        <div style={{ 
          padding: '1.5rem', 
          backgroundColor: '#f8fafc', 
          borderRadius: '8px',
          border: '1px solid #e2e8f0'
        }}>
          <h4 style={{ margin: 0, marginBottom: '1rem', color: '#475569' }}>Order Total</h4>
          <div style={{ fontSize: '28px', fontWeight: 'bold', color: '#1e40af', marginBottom: '0.5rem' }}>
            {formatCurrency(order.total_amount)}
          </div>
          <div style={{ fontSize: '14px', color: '#64748b' }}>
            <div>Subtotal: {formatCurrency(order.subtotal)}</div>
            <div>Tax: {formatCurrency(order.tax_amount)}</div>
            <div>Shipping: {formatCurrency(order.shipping_amount)}</div>
          </div>
        </div>

        {order.external_order_id && (
          <div style={{ 
            padding: '1.5rem', 
            backgroundColor: '#f8fafc', 
            borderRadius: '8px',
            border: '1px solid #e2e8f0'
          }}>
            <h4 style={{ margin: 0, marginBottom: '1rem', color: '#475569' }}>External Reference</h4>
            <div style={{ fontSize: '14px', color: '#64748b', display: 'grid', gap: '6px' }}>
              <div><strong>System:</strong> {order.external_system || 'N/A'}</div>
              <div><strong>Order ID:</strong> {order.external_order_id}</div>
              {(order as any).awb_number && <div><strong>AWB Number:</strong> <span style={{ fontFamily: 'monospace', fontWeight: 600, color: '#1e293b' }}>{(order as any).awb_number}</span></div>}
              {(order as any).shipping_provider && <div><strong>Shipping Vendor:</strong> {(order as any).shipping_provider}</div>}
              {(order as any).shipping_status && <div><strong>Shipping Status:</strong> <span style={{ textTransform: 'capitalize' }}>{String((order as any).shipping_status).replace('_', ' ')}</span></div>}
            </div>
          </div>
        )}
      </div>

      {/* Customer and Shipping Information */}
      <div className="form-grid-2" style={{ gap: '1.5rem', marginBottom: '2rem' }}>
        {/* Customer Information */}
        <div style={{ 
          padding: '1.5rem', 
          backgroundColor: '#f8fafc', 
          borderRadius: '8px',
          border: '1px solid #e2e8f0'
        }}>
          <h4 style={{ margin: 0, marginBottom: '1rem', color: '#475569' }}>Customer Information</h4>
          {order.customer ? (
            <div style={{ fontSize: '14px', color: '#1e293b' }}>
              <div style={{ marginBottom: '0.5rem' }}>
                <strong>{order.customer.name}</strong>
              </div>
              {order.customer.email && (
                <div style={{ marginBottom: '0.25rem' }}>
                  📧 {order.customer.email}
                </div>
              )}
              {order.customer.phone && (
                <div style={{ marginBottom: '0.25rem' }}>
                  📞 {order.customer.phone}
                </div>
              )}
              {order.customer.billing_address && (
                <div style={{ marginTop: '0.75rem', color: '#64748b' }}>
                  <strong>Billing Address:</strong><br />
                  {order.customer.billing_address}
                </div>
              )}
            </div>
          ) : (
            <div style={{ color: '#64748b' }}>No customer information available</div>
          )}
        </div>

        {/* Shipping Address */}
        <div style={{ 
          padding: '1.5rem', 
          backgroundColor: '#f8fafc', 
          borderRadius: '8px',
          border: '1px solid #e2e8f0'
        }}>
          <h4 style={{ margin: 0, marginBottom: '1rem', color: '#475569' }}>Shipping Address</h4>
          {order.shipping_address ? (
            <div style={{ fontSize: '14px', color: '#1e293b' }}>
              <div style={{ marginBottom: '0.5rem' }}>
                <strong>{order.shipping_address.recipient_name}</strong>
              </div>
              <div>{order.shipping_address.address_line1}</div>
              {order.shipping_address.address_line2 && (
                <div>{order.shipping_address.address_line2}</div>
              )}
              <div>
                {order.shipping_address.city}
                {order.shipping_address.state && `, ${order.shipping_address.state}`}
                {' '}{order.shipping_address.postal_code}
              </div>
              <div>{order.shipping_address.country}</div>
              {order.shipping_address.phone && (
                <div style={{ marginTop: '0.5rem' }}>
                  📞 {order.shipping_address.phone}
                </div>
              )}
            </div>
          ) : (
            <div style={{ color: '#64748b' }}>No shipping address available</div>
          )}
        </div>
      </div>

      {/* Warehouse Assignment */}
      <div style={{ 
        padding: '1.5rem', 
        backgroundColor: '#f8fafc', 
        borderRadius: '8px',
        border: '1px solid #e2e8f0',
        marginBottom: '2rem'
      }}>
        <h4 style={{ margin: 0, marginBottom: '1rem', color: '#475569' }}>Warehouse</h4>
        <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
          <div style={{ fontSize: '14px', color: '#1e293b' }}>
            {order.warehouse_id ? (
              <strong>{warehouses.find(w => w.id === order.warehouse_id)?.name || `Warehouse #${order.warehouse_id}`}</strong>
            ) : (
              <span style={{ color: '#94a3b8' }}>No warehouse assigned</span>
            )}
          </div>
          {(userRole === 'admin' || userRole === 'manager') && (
            <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
              <select
                value={selectedWarehouseId}
                onChange={(e) => setSelectedWarehouseId(e.target.value)}
                style={{ padding: '5px 10px', fontSize: '13px', border: '1px solid #e2e8f0', borderRadius: '6px', background: 'white' }}
              >
                <option value="">Change warehouse...</option>
                {warehouses.filter(w => w.id !== order.warehouse_id).map((w: any) => (
                  <option key={w.id} value={w.id}>{w.name}</option>
                ))}
              </select>
              {selectedWarehouseId && (
                <button
                  onClick={() => handleWarehouseChange(selectedWarehouseId)}
                  disabled={changingWarehouse}
                  style={{ padding: '5px 12px', fontSize: '12px', fontWeight: 600, borderRadius: '6px', border: 'none', background: '#0d9488', color: 'white', cursor: 'pointer' }}
                >
                  {changingWarehouse ? '...' : 'Update'}
                </button>
              )}
            </div>
          )}
        </div>
      </div>

      {/* Line Items Table */}
      <div style={{ marginBottom: '2rem' }}>
        <h4 style={{ marginBottom: '1rem', color: '#475569' }}>Order Items</h4>
        <div className="table-container">
          <table className="table">
            <thead>
              <tr>
                <th>SKU</th>
                <th>Product</th>
                <th style={{ textAlign: 'right' }}>Quantity</th>
                <th style={{ textAlign: 'right' }}>Unit Price</th>
                <th style={{ textAlign: 'right' }}>Line Total</th>
                <th>Inventory Status</th>
              </tr>
            </thead>
            <tbody>
              {order.items && order.items.length > 0 ? (
                order.items.map((item) => {
                  const inventory = inventoryMap.get(item.product_id);
                  return (
                    <tr key={item.id}>
                      <td><code>{item.sku}</code></td>
                      <td>
                        <strong>{item.product_name}</strong>
                        {item.notes && (
                          <div style={{ fontSize: '12px', color: '#64748b', marginTop: '4px' }}>
                            {item.notes}
                          </div>
                        )}
                      </td>
                      <td style={{ textAlign: 'right' }}>{item.quantity}</td>
                      <td style={{ textAlign: 'right' }}>{formatCurrency(item.unit_price)}</td>
                      <td style={{ textAlign: 'right' }}>
                        <strong>{formatCurrency(item.line_total)}</strong>
                      </td>
                      <td>
                        {inventory ? (
                          <div style={{ fontSize: '12px' }}>
                            <div>Available: <strong style={{ color: '#16a34a' }}>{inventory.quantity_available}</strong></div>
                            <div style={{ color: '#64748b' }}>On Hand: {inventory.quantity_on_hand}</div>
                            <div style={{ color: '#64748b' }}>Reserved: {inventory.quantity_reserved}</div>
                          </div>
                        ) : (
                          <span style={{ fontSize: '12px', color: '#64748b' }}>N/A</span>
                        )}
                      </td>
                    </tr>
                  );
                })
              ) : (
                <tr>
                  <td colSpan={6} style={{ textAlign: 'center', padding: '2rem', color: '#64748b' }}>
                    No items in this order
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      </div>

      {/* Status History Timeline */}
      {statusHistory.length > 0 && (
        <div style={{ marginBottom: '2rem' }}>
          <h4 style={{ marginBottom: '1rem', color: '#475569' }}>Status History</h4>
          <div style={{ 
            padding: '1.5rem', 
            backgroundColor: '#f8fafc', 
            borderRadius: '8px',
            border: '1px solid #e2e8f0'
          }}>
            {statusHistory.map((history, index) => (
              <div 
                key={history.id}
                style={{ 
                  display: 'flex',
                  alignItems: 'flex-start',
                  marginBottom: index < statusHistory.length - 1 ? '1.5rem' : 0,
                  paddingBottom: index < statusHistory.length - 1 ? '1.5rem' : 0,
                  borderBottom: index < statusHistory.length - 1 ? '1px solid #e2e8f0' : 'none'
                }}
              >
                <div style={{ 
                  width: '12px', 
                  height: '12px', 
                  borderRadius: '50%', 
                  backgroundColor: '#3b82f6',
                  marginRight: '1rem',
                  marginTop: '4px',
                  flexShrink: 0
                }} />
                <div style={{ flex: 1 }}>
                  <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '0.25rem' }}>
                    <div>
                      {history.from_status && (
                        <span style={{ color: '#64748b' }}>{history.from_status} → </span>
                      )}
                      <strong style={{ color: '#1e293b' }}>{history.to_status}</strong>
                    </div>
                    <div style={{ fontSize: '14px', color: '#64748b' }}>
                      {formatDate(history.created_at)}
                    </div>
                  </div>
                  {history.notes && (
                    <div style={{ fontSize: '14px', color: '#64748b', marginTop: '0.5rem' }}>
                      {history.notes}
                    </div>
                  )}
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Status Transition Actions */}
      {availableTransitions.length > 0 && (
        <div style={{ 
          padding: '1.5rem', 
          backgroundColor: '#f8fafc', 
          borderRadius: '8px',
          border: '1px solid #e2e8f0'
        }}>
          <h4 style={{ margin: 0, marginBottom: '1rem', color: '#475569' }}>Change Order Status</h4>
          <div style={{ display: 'flex', gap: '0.5rem', flexWrap: 'wrap' }}>
            {availableTransitions.map(status => (
              <button
                key={status}
                className="btn btn-primary"
                onClick={() => handleStatusChange(status)}
                disabled={statusUpdateLoading}
                style={{ textTransform: 'capitalize' }}
              >
                {statusUpdateLoading ? 'Updating...' : `Mark as ${status}`}
              </button>
            ))}
          </div>
        </div>
      )}

      {/* Order Notes */}
      {order.notes && (
        <div style={{ 
          marginTop: '2rem',
          padding: '1.5rem', 
          backgroundColor: '#fffbeb', 
          borderRadius: '8px',
          border: '1px solid #fbbf24'
        }}>
          <h4 style={{ margin: 0, marginBottom: '0.5rem', color: '#92400e' }}>Order Notes</h4>
          <div style={{ color: '#78350f' }}>{order.notes}</div>
        </div>
      )}

      {/* Ship-out details popup (allocated → shipped) */}
      <ShipOrderModal
        isOpen={showShipModal}
        orderNumber={order.order_number}
        loading={statusUpdateLoading}
        onConfirm={handleShipConfirm}
        onCancel={() => setShowShipModal(false)}
      />
    </div>
  );
};

export default OrderDetail;
